import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Store, Repeat, Clock, PlusCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SponsorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/my/listings").then((r) => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => { if (user === false) navigate("/auth"); }, [user, navigate]);

  if (!user) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  const cancelRenew = async (id) => {
    if (!window.confirm("Turn off auto-renew? Your placement stays active until the current period ends and won't renew after that. Charges already made are non-refundable.")) return;
    try {
      await api.post(`/payments/subscriptions/${id}/cancel`);
      toast.success("Auto-renew turned off");
      load();
    } catch { toast.error("Could not turn off auto-renew"); }
  };

  const daysLeft = (until) => Math.max(0, Math.ceil((new Date(until) - Date.now()) / 86400000));

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 md:px-8" data-testid="sponsor-dashboard">
      <div className="mb-8 flex items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-serif text-3xl font-bold tracking-tight md:text-4xl"><Store size={26} /> Business dashboard</h1>
          <p className="mt-2 text-muted-foreground">Track your listings and featured placements.</p>
        </div>
        <Link to="/sponsor" data-testid="dashboard-feature-btn" className="hidden shrink-0 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground sm:flex">
          <PlusCircle size={16} /> Feature a listing
        </Link>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading your listings…</p>
      ) : listings.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="dashboard-empty">
          <p className="text-muted-foreground">You haven't added a business yet.</p>
          <Link to="/sponsor" className="mt-3 inline-block rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Add & feature your business</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {listings.map((p) => {
            const active = p.is_sponsored && p.sponsored_until && new Date(p.sponsored_until) > new Date();
            return (
              <div key={p.id} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center" data-testid={`dashboard-listing-${p.id}`}>
                {p.cover_image && <img src={p.cover_image} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-lg font-semibold">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.category} · {p.city}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {active ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent/20 px-2.5 py-1 text-xs font-semibold text-accent-foreground" data-testid={`dashboard-status-${p.id}`}>
                        <Sparkles size={12} /> Featured · {daysLeft(p.sponsored_until)} days left
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground" data-testid={`dashboard-status-${p.id}`}>
                        <Clock size={12} /> Not currently featured
                      </span>
                    )}
                    {p.auto_renew && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-1 text-xs font-semibold text-secondary" data-testid={`dashboard-autorenew-${p.id}`}>
                        <Repeat size={12} /> Auto-renews {p.sponsored_until ? new Date(p.sponsored_until).toLocaleDateString() : "monthly"}
                      </span>
                    )}
                    {p.status === "needs_review" && (
                      <span className="rounded-full bg-accent/20 px-2.5 py-1 text-xs font-semibold text-accent-foreground">Pending review</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {p.auto_renew ? (
                    <button onClick={() => cancelRenew(p.id)} data-testid={`dashboard-cancel-${p.id}`}
                      className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:border-destructive hover:text-destructive">
                      Turn off auto-renew
                    </button>
                  ) : (
                    <Link to="/sponsor" data-testid={`dashboard-boost-${p.id}`}
                      className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
                      Feature this
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground">Cancelling keeps your placement active until the current period ends. Charges already made are non-refundable, and partial periods are not refunded.</p>
        </div>
      )}
    </div>
  );
}
